import React, { useState, useEffect, useRef } from 'react';
import { F, C, btn, inp } from '../../config/styles';
import { StudioForm } from '../products/forms';
import { isVideoMedia, syncImagesFields, exportMediaUrls } from '../../utils/products';
import { BulkCrop } from '../products/CropModal';
import { croppedItem } from '../../utils/crop';
import { uploadMedia } from '../../utils/mediaUpload';
import { useTenantConfig } from '../../hooks/useTenant';
import { CollectionImageUploadModal } from './Dashboard';
import { ImportModal } from '../products/modals';

const toHandle = s => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const STATUS_STYLE = {
  empty:    { background: '#f3f4f6', color: '#6b7280' },
  progress: { background: '#dbeafe', color: '#1d4ed8' },
  review:   { background: '#fef3c7', color: '#92400e' },
  approved: { background: '#dcfce7', color: '#065f46' },
};
const STATUS_LABEL = { empty: 'Not started', progress: 'In progress', review: 'In review', approved: 'Approved' };

function studioStatus(p) { return p.sectionStatus?.studio || 'empty'; }

function exportImagesCsv(products, collectionName) {
  const rows = [['Handle', 'Image Src', 'Image Position', 'Image Alt Text']];
  for (const p of products) {
    // Videos are included — Matrixify imports them from Image Src (Shopify's own CSV import
    // would reject them; use Matrixify or the Shopify push).
    const urls = exportMediaUrls(p);
    if (!urls.length) continue;
    const handle = p.handle || p.skuBase || p.id;
    const nameOf = Object.fromEntries((p.images || []).map(m => [m?.url, m?.filename || '']));
    urls.forEach((url, i) => rows.push([handle, url, i + 1, nameOf[url] || '']));
  }
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${collectionName || 'collection'}-images.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── Product card ──────────────────────────────────────────────────────────────

function ProductCard({ product, onClick, isSelected }) {
  const status   = studioStatus(product);
  const style    = STATUS_STYLE[status] || STATUS_STYLE.empty;
  const media    = product.images || [];
  const imageUrl = product.imageUrl || media.find(m => !isVideoMedia(m))?.url;
  const vidCount = media.filter(isVideoMedia).length;
  const imgCount = media.length - vidCount;

  return (
    <div onClick={onClick} style={{
      background: C.white, borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
      border: `2px solid ${isSelected ? C.brand : C.e8}`,
      boxShadow: isSelected ? `0 0 0 3px ${C.brand}22` : '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}>
      <div style={{ aspectRatio: '3/4', background: '#f3f4f6', position: 'relative', overflow: 'hidden' }}>
        {imageUrl ? (
          <img src={imageUrl} alt={product.title} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="m21 15-5-5L5 21"/>
            </svg>
          </div>
        )}
        {(imgCount > 0 || vidCount > 0) && (
          <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 10, fontFamily: F.mono, padding: '2px 6px', borderRadius: 4, backdropFilter: 'blur(4px)' }}>
            {[imgCount > 0 && `${imgCount} ${imgCount === 1 ? 'image' : 'images'}`, vidCount > 0 && `${vidCount} ${vidCount === 1 ? 'video' : 'videos'}`].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, fontFamily: F.display, lineHeight: 1.35, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {product.title || product.handle || product.skuBase || 'Untitled'}
        </div>
        {product.skuBase && (
          <div style={{ fontSize: 11, color: C.muted, fontFamily: F.mono, marginBottom: 8 }}>{product.skuBase}</div>
        )}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, letterSpacing: '0.03em', ...style }}>
          {STATUS_LABEL[status] || status}
        </div>
      </div>
    </div>
  );
}

// ─── Studio drawer ─────────────────────────────────────────────────────────────

// Drawer width: draggable from its left edge, remembered per browser (a convenience — falls
// back to the default if storage is unavailable). Double-click the edge to reset.
const DRAWER_DEFAULT = 760, DRAWER_MIN = 420, DRAWER_KEY = 'studio-drawer-width';
const clampWidth = w => Math.max(DRAWER_MIN, Math.min(w, window.innerWidth * 0.95));
function loadDrawerWidth() {
  try { const w = Number(localStorage.getItem(DRAWER_KEY)); return w > 0 ? clampWidth(w) : clampWidth(DRAWER_DEFAULT); }
  catch { return clampWidth(DRAWER_DEFAULT); }
}

function StudioDrawer({ product, allProducts, onChange, onClose }) {
  const [width, setWidth] = useState(loadDrawerWidth);
  const resizing = useRef(false);
  useEffect(() => {
    const move = e => { if (resizing.current) setWidth(clampWidth(window.innerWidth - e.clientX)); };
    const up = () => {
      if (!resizing.current) return;
      resizing.current = false;
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      setWidth(w => { try { localStorage.setItem(DRAWER_KEY, String(Math.round(w))); } catch {} return w; });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, []);
  const startResize = e => {
    e.preventDefault();
    resizing.current = true;
    document.body.style.cursor = 'ew-resize'; document.body.style.userSelect = 'none';
  };
  const resetWidth = () => {
    setWidth(clampWidth(DRAWER_DEFAULT));
    try { localStorage.removeItem(DRAWER_KEY); } catch {}
  };

  const currentIdx = allProducts.findIndex(p => p.id === product.id);
  const prev = currentIdx > 0 ? allProducts[currentIdx - 1] : null;
  const next = currentIdx < allProducts.length - 1 ? allProducts[currentIdx + 1] : null;

  const [title,  setTitle]  = useState(product.title  || '');
  const [handle, setHandle] = useState(product.handle || '');
  const backdropMouseDownRef = useRef(false);

  useEffect(() => {
    setTitle(product.title   || '');
    setHandle(product.handle || '');
  }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTitleBlur = () => {
    if (title !== product.title) {
      const newHandle = handle.trim() || toHandle(title);
      setHandle(newHandle);
      onChange({ ...product, title, handle: newHandle }, false);
    }
  };

  const handleHandleBlur = () => {
    const cleaned = toHandle(handle || title);
    setHandle(cleaned);
    if (cleaned !== product.handle) onChange({ ...product, handle: cleaned }, false);
  };

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      <div
        onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
        onClick={e => { if (backdropMouseDownRef.current && e.target === e.currentTarget) onClose(); }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, backdropFilter: 'blur(1px)' }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width, maxWidth: '95vw',
        background: C.white, zIndex: 101, display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
      }}>
        <div onPointerDown={startResize} onDoubleClick={resetWidth}
          title="Drag to resize · double-click to reset"
          style={{ position: 'absolute', left: -4, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 1 }}>
          <div style={{ position: 'absolute', left: 2, top: '50%', transform: 'translateY(-50%)', width: 4, height: 48, borderRadius: 2, background: C.e8 }} />
        </div>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.e8}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                onBlur={handleTitleBlur}
                placeholder="Product title…"
                style={{ ...inp(), fontSize: 14, fontWeight: 600, fontFamily: F.display, width: '100%', marginBottom: 6 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>Handle:</span>
                <input
                  value={handle}
                  onChange={e => setHandle(e.target.value)}
                  onBlur={handleHandleBlur}
                  placeholder={toHandle(title) || 'product-handle'}
                  style={{ ...inp(), fontSize: 11, fontFamily: F.mono, flex: 1 }}
                />
              </div>
              {product.skuBase && (
                <div style={{ fontSize: 11, color: C.muted, fontFamily: F.mono, marginTop: 4 }}>{product.skuBase}</div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, lineHeight: 1, padding: 4, flexShrink: 0 }}>×</button>
          </div>
          {(prev || next) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => prev && onChange(prev, true)} disabled={!prev}
                style={{ ...btn('secondary'), fontSize: 12, padding: '5px 12px', opacity: prev ? 1 : 0.35 }}>← Previous</button>
              <button onClick={() => next && onChange(next, true)} disabled={!next}
                style={{ ...btn('secondary'), fontSize: 12, padding: '5px 12px', opacity: next ? 1 : 0.35 }}>Next →</button>
              <span style={{ fontSize: 11, color: C.muted, display: 'flex', alignItems: 'center', marginLeft: 4 }}>
                {currentIdx + 1} / {allProducts.length}
              </span>
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          <StudioForm p={product} onChange={updated => onChange(updated, false)} />
        </div>
      </div>
    </>
  );
}

// ─── Main view ─────────────────────────────────────────────────────────────────

export function StudioProductsView({ collection, onUpdateProduct, onUpdateProducts }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showUpload,      setShowUpload]      = useState(false);
  const [showImport,      setShowImport]      = useState(false);
  const [search,          setSearch]          = useState('');

  const products = collection?.products || [];
  const { cropPresets } = useTenantConfig();

  // "Crop all" saves product by product over a long async run, so read the latest products and
  // updater through refs — the ones captured when the run started would drop earlier results.
  const latest = useRef({ products, onUpdateProducts });
  latest.current = { products, onUpdateProducts };
  const applyGroupCrop = async (pid, results) => {
    const base = latest.current.products.find(p => p.id === pid);
    if (!base) return;
    const uploaded = {};
    for (const [i, { file }] of Object.entries(results)) uploaded[i] = await uploadMedia(file, pid);
    const images = (base.images || []).map((m, i) => uploaded[i] ? croppedItem(m, uploaded[i], results[i].crop) : m);
    const updated = { ...base, images, ...syncImagesFields(images), updatedAt: new Date().toISOString() };
    const next = latest.current.products.map(p => p.id === pid ? updated : p);
    latest.current.products = next;   // don't wait for the re-render before the next product
    latest.current.onUpdateProducts(next);
  };

  const liveSelectedProduct = selectedProduct
    ? (products.find(p => p.id === selectedProduct.id) ?? selectedProduct)
    : null;

  const filtered = search.trim()
    ? products.filter(p => [p.title, p.skuBase, p.handle].some(s => s?.toLowerCase().includes(search.toLowerCase())))
    : products;

  const handleDrawerChange = (updated, isNavigating) => {
    if (isNavigating) setSelectedProduct(updated);
    else onUpdateProduct(updated);
  };

  const handleDeleteAllImages = () => {
    const total = products.reduce((n, p) => n + (p.images?.length || 0), 0);
    if (!total) return;
    if (!window.confirm(`Remove all ${total} image${total !== 1 ? 's' : ''} from every product in this collection? This cannot be undone.`)) return;
    onUpdateProducts(products.map(p => ({ ...p, images: [], imageUrl: '', additionalImages: '' })));
    setSelectedProduct(null);
  };

  const handleAssign = productImagesMap => {
    const updated = products.map(p => {
      const newImgs = productImagesMap[p.id];
      if (!newImgs?.length) return p;
      const all = [...(p.images || []), ...newImgs];
      return { ...p, images: all, ...syncImagesFields(all) };
    });
    onUpdateProducts(updated);
  };

  const counts = products.reduce((acc, p) => {
    const s = studioStatus(p); acc[s] = (acc[s] || 0) + 1; return acc;
  }, {});

  return (
    <div style={{ background: '#f8f9fa', minHeight: 'calc(100vh - 94px)', fontFamily: F.body }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 24px 48px' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.ph} strokeWidth="2"
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
              style={{ ...inp(), paddingLeft: 30, width: 220, fontSize: 13 }} />
          </div>
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.muted }}>Clear</button>
          )}
          <div style={{ flex: 1 }} />
          {['approved', 'review', 'progress', 'empty'].map(s => counts[s] ? (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, ...STATUS_STYLE[s], padding: '3px 10px', borderRadius: 20 }}>
              <span style={{ fontWeight: 600 }}>{counts[s]}</span>
              <span style={{ opacity: 0.75 }}>{STATUS_LABEL[s]}</span>
            </div>
          ) : null)}
          <div style={{ fontSize: 12, color: C.muted }}>{products.length} product{products.length !== 1 ? 's' : ''}</div>
          <button onClick={() => setShowImport(true)} style={{ ...btn('secondary'), fontSize: 13 }}>Import products</button>
          <button onClick={() => setShowUpload(true)} style={{ ...btn('secondary'), fontSize: 13 }}>Upload images & video</button>
          <BulkCrop modal label="Crop all…" presets={cropPresets || []} onGroupDone={applyGroupCrop}
            groups={products.map(p => ({ id: p.id, label: p.title || p.handle, images: p.images || [] }))} />
          <button onClick={() => exportImagesCsv(products, collection?.name)} style={{ ...btn('secondary'), fontSize: 13 }}>Export images CSV</button>
          <button onClick={handleDeleteAllImages} style={{ ...btn('secondary'), fontSize: 13, color: '#c00', borderColor: '#fca5a5' }}>Delete all images</button>
        </div>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {filtered.map(product => (
              <ProductCard key={product.id} product={product}
                onClick={() => setSelectedProduct(product)}
                isSelected={liveSelectedProduct?.id === product.id} />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: C.muted, fontSize: 14 }}>
            {search ? `No products match "${search}"` : 'No products in this collection yet'}
          </div>
        )}
      </div>

      {liveSelectedProduct && (
        <StudioDrawer product={liveSelectedProduct} allProducts={filtered}
          onChange={handleDrawerChange} onClose={() => setSelectedProduct(null)} />
      )}

      {showImport && (
        <ImportModal
          existingProducts={products}
          onImport={prods => {
            const byHandle = new Map(products.map((p, i) => p.handle  ? [p.handle,  i] : null).filter(Boolean));
            const bySku    = new Map(products.map((p, i) => p.skuBase ? [p.skuBase, i] : null).filter(Boolean));
            const merged = [...products];
            prods.forEach(p => {
              if (p.handle && byHandle.has(p.handle))   merged[byHandle.get(p.handle)] = p;
              else if (p.skuBase && bySku.has(p.skuBase)) merged[bySku.get(p.skuBase)] = p;
              else merged.push(p);
            });
            onUpdateProducts(merged);
          }}
          onClose={() => setShowImport(false)}
        />
      )}

      {showUpload && (
        <CollectionImageUploadModal products={products} onAssign={handleAssign} onClose={() => setShowUpload(false)} studioMode />
      )}
    </div>
  );
}
